import type { Lead } from '../../types/crm'
import { cleanCell, isPlaceholder } from '../../data/transform'
import { getLeadEmail, getLeadPhone, getProspect, isProspectOnly } from './unifiedLeadFields'

export type LeadContact =
  | { kind: 'phone'; value: string; href: string }
  | { kind: 'email'; value: string; href: string }
  | { kind: 'none'; value: ' - '; href: null }

function meaningfulName(value: unknown): string {
  return isPlaceholder(value) ? '' : cleanCell(value)
}

export function getLeadDisplayName(lead: Lead): string {
  const companyName = meaningfulName(lead.company) || meaningfulName(lead.organization?.name)
  if (companyName) return companyName

  return (
    meaningfulName(lead.owner)
    || meaningfulName(lead.opportunity?.name)
    || meaningfulName(getProspect(lead)?.person)
    || 'Unnamed prospect'
  )
}

/**
 * The secondary line under the lead name. A prospecting-only record has no
 * separate company, so its person is already the headline and the niche is
 * shown instead of repeating the name.
 */
export function getLeadSubtitle(lead: Lead): string {
  if (!isProspectOnly(lead)) return lead.owner
  return meaningfulName(getProspect(lead)?.niche)
}

export function getLeadContact(lead: Lead): LeadContact {
  const phone = getLeadPhone(lead)
  const phoneDigits = phone.replace(/\D/g, '')
  if (phoneDigits.length >= 7) {
    return { kind: 'phone', value: phone, href: `tel:${phone.replace(/[^+\d]/g, '')}` }
  }

  const email = getLeadEmail(lead)
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return { kind: 'email', value: email, href: `mailto:${email}` }
  }

  return { kind: 'none', value: ' - ', href: null }
}

export function sortLeadsAlphabetically<TLead extends Lead>(leads: readonly TLead[]): TLead[] {
  return [...leads].sort((first, second) => {
    const comparison = getLeadDisplayName(first).localeCompare(
      getLeadDisplayName(second),
      undefined,
      { sensitivity: 'base', numeric: true },
    )
    return comparison || first.order - second.order
  })
}
